import React from 'react';
import { Typography, Paper, Grid, Card, CardContent, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import SettingsIcon from '@mui/icons-material/Settings';
import BarChartIcon from '@mui/icons-material/BarChart';
import SaveIcon from '@mui/icons-material/Save';
import BugReportIcon from '@mui/icons-material/BugReport';

const AITalkerPlayground = () => {
  const features = [
    {
      icon: <ChatIcon />,
      title: "Real-time Interaction",
      description: "Engage in live conversations with your AI Talker to test its responses and behavior."
    },
    {
      icon: <SettingsIcon />,
      title: "Configuration Testing",
      description: "Experiment with different settings and see how they affect the AI's performance in real-time."
    },
    {
      icon: <BarChartIcon />,
      title: "Performance Analytics",
      description: "Get instant feedback on response times, accuracy, and other key performance metrics."
    },
    {
      icon: <SaveIcon />,
      title: "Scenario Saving",
      description: "Save and reload different conversation scenarios for consistent testing and comparison."
    },
    {
      icon: <BugReportIcon />,
      title: "Debug Mode",
      description: "Access detailed logs and trace the AI's decision-making process for each response."
    }
  ];

  const useCases = [
    "Testing new conversation flows",
    "Evaluating language understanding capabilities",
    "Assessing the AI's ability to handle complex queries",
    "Comparing different personality settings",
    "Training team members on AI Talker capabilities",
    "Demonstrating AI Talker to stakeholders or clients"
  ];

  return (
    <Paper elevation={0}>
      <Typography variant="h4" gutterBottom>AI Talker Playground</Typography>
      <Typography paragraph>
        The AI Talker Playground is an interactive environment where you can test, refine, and experiment 
        with your AI Talker in real-time. This powerful tool allows you to experience your AI Talker's 
        capabilities firsthand and make informed decisions about its configuration and deployment.
      </Typography>

      <Typography variant="h6" gutterBottom>Key Features:</Typography>
      <Grid container spacing={3}>
        {features.map((feature, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card>
              <CardContent>
                {feature.icon}
                <Typography variant="h6" component="div">
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography variant="h6" style={{ marginTop: '20px' }}>How to Use the Playground:</Typography>
      <Typography paragraph>
        1. <strong>Access the Playground:</strong> Navigate to the AI Talker section in your dashboard and select the Playground option.
      </Typography>
      <Typography paragraph>
        2. <strong>Choose a Configuration:</strong> Select an existing configuration or create a new one to test.
      </Typography>
      <Typography paragraph>
        3. <strong>Start a Conversation:</strong> Begin interacting with your AI Talker by typing or speaking (if voice input is enabled).
      </Typography>
      <Typography paragraph>
        4. <strong>Analyze Performance:</strong> Review the real-time analytics to assess the AI's performance.
      </Typography>
      <Typography paragraph>
        5. <strong>Adjust Settings:</strong> Make changes to the configuration and immediately see how they affect the AI's behavior.
      </Typography>
      <Typography paragraph>
        6. <strong>Save and Share:</strong> Save interesting conversations or configurations for future reference or to share with your team.
      </Typography>

      <Typography variant="h6" style={{ marginTop: '20px' }}>Use Cases for the Playground:</Typography>
      <List>
        {useCases.map((useCase, index) => (
          <ListItem key={index}>
            <ListItemIcon>
              <ChatIcon />
            </ListItemIcon>
            <ListItemText primary={useCase} />
          </ListItem>
        ))}
      </List>

      <Typography paragraph style={{ marginTop: '20px' }}>
        The AI Talker Playground is an essential tool for developers, content creators, and business 
        stakeholders to ensure that the AI Talker meets your specific needs and performs as expected 
        in various scenarios. By thoroughly testing and refining your AI Talker in the Playground, 
        you can confidently deploy a solution that delivers exceptional user experiences.
      </Typography>
      <Typography paragraph>
        Remember, the more you interact with and test your AI Talker in the Playground, the better 
        you'll understand its capabilities and potential areas for improvement. Don't hesitate to 
        experiment with different approaches and configurations to find the optimal setup for your 
        unique use case.
      </Typography>
    </Paper>
  );
};

export default AITalkerPlayground;
